import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { FeatureCollection, LineString, MultiLineString } from "geojson";
import DeckGL from "@deck.gl/react";
import { GeoJsonLayer, IconLayer, TextLayer } from "@deck.gl/layers";
import { LinearInterpolator } from "@deck.gl/core";
import {
  _MapContext as MapContext,
  NavigationControl,
  StaticMap,
} from "react-map-gl";
import Style from "./Map.module.css";
import { ReactComponent as SatelliteIcon } from "../../assets/satellite.svg";
import { ReactComponent as WorldIcon } from "../../assets/world.svg";
import DroneIconPng from "../../assets/drone.png";
import WhiteMarkerIconPng from "../../assets/icons/white/WHITE.png";
import {
  Icon,
  Switch,
  Checkbox,
  Select,
  DatePicker,
  Radio,
  Input,
  Button,
} from "antd";
import {
  ILocation,
  IServerEvent,
  IEvent,
  IEventProperties,
  IInteractionState,
  IPipelineData,
  IViewState,
  TRANSITION_EVENTS,
  IPipelineProperties,
  IDroneImage,
  IDronePath,
  IDroneJob,
} from "../../interfaces/map.interfaces";
import MapComponentContext from "./map.component.context";
import moment from "moment-timezone";
import {
  INCIDENT_ICON_SIZE,
  PIPELINE_COLOR_PRIMARY,
  PIPELINE_COLOR_SECONDARY,
  PIPELINE_HIGHLIGHT_COLOR,
  SEGMENT_COLOR,
  MAIN_OBJECT_AOI_COLOR,
  GRID_AREA_COLOR,
  PIPELINE_HIGHLIGHT_COLOR_2,
  CROWN_HIGHLIGHT_COLOR,
  CROWN_HIGHLIGHT_COLOR_2,
  isCrownClosureProject,
} from "../../constants/map.consts";
import { Moment } from "moment-timezone/moment-timezone";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import { EditableGeoJsonLayer } from "@nebula.gl/layers";
import { DrawLineStringMode } from "@nebula.gl/edit-modes";
import MapLogic from "../../logic/map.logic";
import {
  calcDuration,
  flyToEvent,
  getEventIcon,
  getImageComparisonEvents,
  getLocationToShow,
  ourEasing,
  zoomIntoEvent,
} from "./map.utils";
import PipelineTooltip from "./PipelineTooltip";
import CompareImages from "./CompareImages";
import ContextMenu from "./ContextMenu";
import EventTooltip from "./EventTooltip";
import {
  canShowStations,
  getHanEventTypes,
  isHanProject,
  isStationEvent,
  STATION_EVENT_TYPE,
} from "../../utils/station.utils";
import AddNewHeliEvent from "./AddNewHeliEvent";
import AddCrownClosureEvent from "./AddCrownClosureEvent";
import { env } from "./../../env";
import VideoModal from "./VideoModal";
import ImageModal from "./ImageModal";
import { DataFilterExtension } from "@deck.gl/extensions";
import {
  getNewestCrownClosureEvent,
  toCrownClosurePathLocations,
  transformCrownClosureData,
} from "../../utils/crownClosureEvent.utils";
import {
  findSviObjectMapEventByEventId,
  getSviObjectMapEvents,
} from "../../services/svidashboard.mock";
import {
  DREAM_USER_EMAIL,
  isDreamUser,
} from "../../constants/dream.constants";

interface IInitialView {
  zoom: number;
  longitude: number;
  latitude: number;
}

interface IMapProps {
  pipelineData: IPipelineData | undefined;
  locationsData: ILocation[];
  crownClosureData: ILocation[];
  crownClosureGeoJsonData: IPipelineData | undefined;
  pipelineLastCheckedUtc: Moment | undefined;
  initialView: IInitialView | undefined;
}

export interface IDeckGlInfoObject {
  index: number;
  x: number;
  y: number;
  object: any;
  coordinate: any;
}

export interface ICompareImages {
  eventBefore?: IEventProperties;
  eventAfter?: IEventProperties;
  sliderController?: number;
}

interface IDragOrig {
  dragStartX: number;
  dragStartY: number;
  origX: number;
  origY: number;
}

const DEFAULT_VIEW_ANGLE = {
  pitch: 0,
  bearing: 0,
};

const normalMapStyle = "mapbox://styles/mapbox/outdoors-v11";
const satelliteMapStyle = "mapbox://styles/mapbox/satellite-v9";

const serverEventToEvent = function (serverEvent: IServerEvent): IEvent {
  let result = {
    type: "Point",
    opacity: 1,
    coordinates: serverEvent.geometry.coordinates,
    properties: serverEvent.properties,
  } as IEvent;

  // we use this later on to check that an object we clicked on is an event,
  // so we ensure that this type is really written here - rather than whatever
  // nonsense might come from the backend ^^'
  return result;
};

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  point1: [number, number],
  point2: [number, number]
): number => {
  const R = 6371e3; // Earth's radius in meters
  const lat1 = (point1[1] * Math.PI) / 180;
  const lat2 = (point2[1] * Math.PI) / 180;
  const deltaLat = ((point2[1] - point1[1]) * Math.PI) / 180;
  const deltaLon = ((point2[0] - point1[0]) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) *
      Math.cos(lat2) *
      Math.sin(deltaLon / 2) *
      Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
};

const formatDistance = (distanceInMeters: number): string => {
  if (distanceInMeters < 1000) {
    return `${distanceInMeters.toFixed(2)} m`;
  } else {
    return `${(distanceInMeters / 1000).toFixed(2)} km`;
  }
};

// once a second, check if the map has loaded the non-working compass icon, and if so,
// replace it with the working one
window.setInterval(function () {
  var btns = document.getElementsByClassName("mapboxgl-ctrl-compass-arrow");
  if (btns.length > 0) {
    btns[0].className = "mapboxgl-ctrl-icon mapboxgl-ctrl-compass";
  }
}, 1000);

export default function Map({
  initialView,
  pipelineData,
  locationsData: _locationsData,
  crownClosureData: _crownClosureData,
  crownClosureGeoJsonData: initialCrownClosureGeoJsonData,
  pipelineLastCheckedUtc,
}: IMapProps) {
  const appContext: IAppContext = useContext(AppContext);

  const [language, setLanguage] = useState<string>(null);
  const [currentUser, setCurrentUser] = useState<string>(null);
  const [showMarkers, setShowMarkers] = useState<boolean>(false);
  const [showStations, setShowStations] = useState<boolean>(false);
  const [labelEnabled, setLabelEnabled] = useState<boolean>(null);
  const [enableHeli, setEnableHeli] = useState<boolean>(false);
  const [enableCc, setEnableCc] = useState<boolean>(false);

  const [showSegment, setShowSegment] = useState(false);
  const [segmentsData, setSegmentsData] = useState();
  const [showmainObjectAoi, setShowMainObjectAoiData] = useState(false);
  const [mainObjectAoiData, setMainObjectAoiData] = useState();
  const [showGridArea, setShowGridArea] = useState(false);
  const [gridAreaData, setGridAreaData] = useState([]);
  const [crownClosureData, setcrownClosureData] = useState(_crownClosureData);
  const [markersData, setMarkersData] = useState<IServerEvent[]>([]);

  const [currentlyEditingPipeline, setCurrentlyEditingPipeline] =
    useState<boolean>(false);
  const [locationsData, setLocationsData] = useState(_locationsData);

  const [videoUrl, setVideoUrl] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  // Drone Jobs and Paths
  const [droneJobs, setDroneJobs] = useState<IDroneJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<IDroneJob>();
  // const [selectedDronePaths, setSelectedDronePaths] = useState([]);
  const [showDronePaths, setShowDronePaths] = useState(false);
  const [ShowCrownclosure, setShowCrownclosure] = useState(() =>
    isCrownClosureProject(appContext.projects?.id)
  );
  const [dronePaths, setdronePaths] = useState<IDronePath[]>([]);
  // Drone Images
  const [showDroneImages, setShowDroneImages] = useState(false);
  const [droneImages, setDroneImages] = useState([]);
  const [showDroneImage, setShowDroneImage] = useState(false);
  const [activeDroneImage, setActiveDroneImage] = useState<IDroneImage>();
  const [droneImageUrl, setDroneImageUrl] = useState<string>();
  const [eventsFilterCollapsed, setEventsFilterCollapsed] =
    useState<boolean>(true);

  // Options dropdown state
  const [optionsCollapsed, setOptionsCollapsed] = useState<boolean>(true);

  // Measure Distance state
  const [measureDistanceCollapsed, setMeasureDistanceCollapsed] =
    useState<boolean>(true);
  const [measureMode, setMeasureMode] = useState<"none" | "map" | "manual">(
    "none"
  );
  const [measurePoints, setMeasurePoints] = useState<[number, number][]>([]);
  const [measureHoverPoint, setMeasureHoverPoint] = useState<
    [number, number] | null
  >(null);
  const [manualPoint1, setManualPoint1] = useState<{
    lat: string;
    lng: string;
  }>({ lat: "", lng: "" });
  const [manualPoint2, setManualPoint2] = useState<{
    lat: string;
    lng: string;
  }>({ lat: "", lng: "" });
  const [calculatedDistance, setCalculatedDistance] = useState<number | null>(
    null
  );

  const resetMeasurement = useCallback(() => {
    setMeasurePoints([]);
    setMeasureHoverPoint(null);
    setCalculatedDistance(null);
  }, []);

  const calculatePathDistance = useCallback(
    (points: [number, number][]): number | null => {
      if (!points || points.length < 2) {
        return null;
      }

      return points
        .slice(1)
        .reduce(
          (sum, point, idx) => sum + calculateDistance(points[idx], point),
          0
        );
    },
    []
  );

  const refreshEvents = useCallback(() => {
    // let selectedService =
    //   appContext?.projects?.app === "svi" || appContext?.projects?.app === "red"
    //     ? appContext?.services.sviEventService
    //     : appContext?.services.eventService;

    const serviceMap = {
      svi: appContext?.services.sviEventService,
      red: appContext?.services.liaEventService,
    };

    let selectedService =
      serviceMap[appContext?.projects?.app] ??
      appContext?.services.eventService;

    selectedService.getMyData().then((data) => {
      setLocationsData(data?.data);

      appContext?.services.crownClosureService.getMyData().then((data) => {
        const pathLocations = toCrownClosurePathLocations(data.data);

        const processedData = transformCrownClosureData(pathLocations);
        const separatedGeojsonList = processedData.map(
          (item) => item.separatedGeojson
        );

        const mergedFeatureCollection = {
          type: "FeatureCollection" as const,
          name: "Crown_Closure_Pipeline_Paths",
          crs: {
            type: "name",
            properties: {
              name: "urn:ogc:def:crs:OGC:1.3:CRS84",
            },
          },
          features: separatedGeojsonList.flatMap((fc) =>
            fc.features.map((f) => ({
              type: "Feature" as const,
              geometry: f.geometry,
              properties: f.properties,
            }))
          ),
        } as FeatureCollection<
          LineString | MultiLineString,
          IPipelineProperties
        > & { crs: any };
        setcrownClosureData(data.data);

        interface IExtendedPipelineData extends IPipelineData {
          name: string;
          crs: {
            type: string;
            properties: {
              name: string;
            };
          };
        }

        // Only process if pipelineData exists and has features
        if (pipelineData && pipelineData.features && pipelineData.features.length > 0) {
          const originalFeature = pipelineData.features[0];
          const originalGeometry = originalFeature.geometry;

          const modifiedPipelineData: IExtendedPipelineData = {
            type: "FeatureCollection",
            name: "User_Pipelines",
            crs: {
              type: "name",
              properties: {
                name: "urn:ogc:def:crs:OGC:1.3:CRS84",
              },
            },
            features: mergedFeatureCollection.features.map((feature) => ({
              type: "Feature",
              geometry: {
                coordinates: feature.geometry.coordinates,
                type: originalGeometry.type, // force same type as pipeline geometry
              } as typeof originalGeometry,
              properties: feature.properties,
            })),
          };

          setcurrentgeojsonData(modifiedPipelineData);
        } else {
          // If no pipelineData, just use the mergedFeatureCollection directly
          setcurrentgeojsonData(mergedFeatureCollection as IExtendedPipelineData);
        }
      });
    });
  }, [
    appContext?.services.eventService,
    appContext?.services.sviEventService,
    appContext?.services.crownClosureService,
  ]);

  const getSegmentsData = () => {
    if (!segmentsData && !mainObjectAoiData) {
      const projectId = appContext.projects?.id;
      if (projectId) {
        appContext?.services.segmentService
          .getMyData(projectId)
          .then((data) => {
            const allSegments = data.data;

            // Separate segments based on mainObjectAoi presence
            const mainObjectAoiSegments = allSegments.filter(
              (segment) => segment.properties?.mainObjectAoi
            );

            const otherSegments = allSegments.filter(
              (segment) => !segment.properties?.mainObjectAoi
            );

            // Set state
            setMainObjectAoiData(mainObjectAoiSegments);
            setSegmentsData(otherSegments);
            setShowSegment(true);

            if (mainObjectAoiSegments.length > 0) {
              setShowMainObjectAoiData(true);
            }
          });
      }
    } else {
      setShowSegment(true);
    }
  };

  const getGridAreasData = () => {
    if (!gridAreaData.length) {
      const projectId = appContext.projects?.id;
      if (projectId) {
        appContext?.services.gridService.getMyData(projectId).then((data) => {
          setGridAreaData(data.data);
          setShowGridArea(true);
        });
      }
    } else {
      setShowGridArea(true);
    }
  };

  const getDronePaths = (projectId: string, selectedJob: string) => {
    if (projectId) {
      appContext?.services.droneService
        .getDronePaths(projectId, selectedJob)
        .then((data) => {
          setdronePaths(data.data);
          setShowDronePaths(true);
        });
    }
  };

  const getDroneJobs = () => {
    const projectId = appContext.projects?.id;
    if (projectId) {
      appContext?.services.droneService.getDroneJobs(projectId).then((data) => {
        setDroneJobs(data.data);
        setShowDronePaths(true);
      });
    }
  };

  const getDroneImages = () => {
    if (!droneImages.length) {
      const projectId = appContext.projects?.id;
      if (projectId) {
        appContext?.services.droneService.getImages(projectId).then((data) => {
          setDroneImages(data.data);
          setShowDroneImages(true);
        });
      }
    } else {
      setShowDroneImages(true);
    }
  };

  const getDroneImageUrl = (obj: any) => {
    if (obj.properties.key) {
      setActiveDroneImage(obj);
      appContext.services.droneService
        .getImageUrl(obj.properties.key)
        .then((data) => {
          setDroneImageUrl(data.data);
          setShowDroneImage(true);
        });
    }
  };

  useEffect(() => {
    appContext.services.userService.getMyData().then((data) => {
      setLanguage(data.data.language);
      setCurrentUser(data.data.email);
      setShowMarkers(!!data.data.show_markers);
      setShowStations(!!data.data.show_stations);
    });
    refreshEvents();
  }, [appContext.services.userService, refreshEvents]);

  useEffect(() => {
    if (!showMarkers) {
      return;
    }
    if (markersData.length > 0) {
      return;
    }
    appContext.services.markerService
      .getMyData()
      .then((data) => setMarkersData(data?.data ?? []))
      .catch(() => setMarkersData([]));
  }, [showMarkers, markersData.length, appContext.services.markerService]);

  // set center of demo data as default location until a pipeline is actually loaded from the backend
  let initView: IInitialView = {
    zoom: 10,
    longitude: 9.9794045,
    latitude: 51.703047,
  };

  // re-set the initial view based on the pipeline view, as supplied by the GenericMapData
  if (initialView) {
    initView = initialView;
  }

  // finally, if we are supposed to show a particular location instead, set the initial view based on that
  const [locationToShow, setLocationToShow] = useState<ILocation>();

  if (locationToShow) {
    initView = MapLogic.fitMapToEventBoundingBox(
      locationToShow.events[0].geometry.coordinates
    );
  }

  const initialViewState: IViewState = {
    ...DEFAULT_VIEW_ANGLE,
    ...initView,
  };
  const [previousViewState, setPreviousViewState] =
    useState<IViewState>(initialViewState);
  const [viewState, setViewState] = useState<IViewState>(initialViewState);
  const [mapStyle, setMapStyle] = useState<string>(normalMapStyle);
  const [embeddedApp, setEmbeddedApp] = useState<boolean>(false);
  const [preventNextGenericMapClick, setPreventNextGenericMapClick] =
    useState<boolean>(false);
  const [clickedZoomBtn, setClickedZoomBtn] = useState<boolean>(false);
  const [clickedObject, setClickedObject] = useState<IDeckGlInfoObject | null>(
    null
  );

  useEffect(() => {
    if (appContext.projects?.app === "svi") {
      setEmbeddedApp(true);
      setShowSegment(true);
      getSegmentsData();
      toggleMapStyle();
    } else if (appContext.projects?.app === "red") {
      // Land Invasion Alerts: default to satellite basemap with the
      // monitored area segments switched on when the map loads.
      setShowSegment(true);
      getSegmentsData();
      toggleMapStyle();
    }
  }, [appContext.projects?.app]);

  useEffect(() => {
    setShowCrownclosure(isCrownClosureProject(appContext.projects?.id));
  }, [appContext.projects?.id]);

  // state of the geojson of the editable pipeline
  const [editGeoJson, setEditGeoJson] = useState({
    type: "FeatureCollection",
    features: [],
  });

  /* setHoveredObject is only used to solve a flickering bug when hovering over IconLayer */
  /* eslint-disable-next-line */
  const [hoveredObject, setHoveredObject] = useState<IDeckGlInfoObject | null>({
    index: 0,
    x: 0,
    y: 0,
    object: null,
    coordinate: [0, 0],
  });
  const [compareImages, setCompareImages] = useState<ICompareImages>({});

  const closeTooltip = useCallback(() => {
    // Removed zoom-out logic - keep the same zoom level when closing popup
    setClickedObject(null);
    setClickedZoomBtn(true);
  }, []);

  const handleToggleStations = (checked: boolean) => {
    setShowStations(checked);
    if (!checked) {
      if (isStationEvent(clickedObject?.object?.properties)) {
        closeTooltip();
      }
      setEventsFilter((prev) => ({
        ...prev,
        type: prev.type.filter((t) => t !== STATION_EVENT_TYPE),
      }));
    }
  };

  const handleToggleMarkers = (checked: boolean) => {
    setShowMarkers(checked);
    if (!checked && clickedObject?.object?.isMarker) {
      closeTooltip();
    }
  };

  const [currentPipelineData, setCurrentPipelineData] = useState<
    IPipelineData | undefined
  >(pipelineData);

  const [currentgeojsonData, setcurrentgeojsonData] = useState<
    IPipelineData | undefined
  >(initialCrownClosureGeoJsonData);

  const toggleMapStyle = () => {
    const newMapStyle =
      mapStyle === normalMapStyle ? satelliteMapStyle : normalMapStyle;
    setMapStyle(newMapStyle);
  };

  const closeImageCompare = () => {
    setCompareImages({});
  };

  const openImageCompare = (compareImages: ICompareImages) => {
    setCompareImages(compareImages);
  };

  const zoomIn = (event: IEvent) => {
    !clickedZoomBtn && setPreviousViewState({ ...viewState });
    setViewState(zoomIntoEvent(event, viewState));
  };

  useEffect(() => {
    setLocationToShow(getLocationToShow(locationsData));
    let locationToShow = getLocationToShow(locationsData);
    if (locationToShow) {
      let events = locationToShow.events;
      let imageCompare = getImageComparisonEvents(locationToShow);
      let event = serverEventToEvent(events[0]);

      // open the event callout
      setClickedObject({
        index: 0,
        x: 0,
        y: 0,
        object: event,
        coordinate: {},
      });

      // open the event image comparison view
      openImageCompare(imageCompare);
      setViewState(zoomIntoEvent(event, viewState));

      //   if (appContext.projects?.app === "svi-x") {

      //   setEmbeddedApp(true)
      //   setShowSegment(true)
      //   getSegmentsData()
      //   toggleMapStyle()
      // }
    }
  }, [locationsData]);

  // Ref to track if we've already restored the event modal and the eventId we restored
  const eventRestoredRef = useRef<string | null>(null);
  
  // Check for eventId query parameter to restore event modal when returning from dashboard
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const eventId = urlParams.get('eventId');
    
    // Only restore if we have an eventId and haven't restored this specific event yet
    if (!eventId || eventRestoredRef.current === eventId) {
      return;
    }
    
    // If clickedObject is already set to this event, mark as restored
    if (clickedObject?.object?.properties?.id === eventId) {
      eventRestoredRef.current = eventId;
      return;
    }
    
    // Check for SVI object map events first (doesn't require locationsData)
    const sviObjectEvent = findSviObjectMapEventByEventId(eventId);
    if (sviObjectEvent && isDreamUser(dreamUserEmail)) {
      eventRestoredRef.current = eventId;
      const foundEvent = sviObjectEvent;

      setTimeout(() => {
        const newX = document.getElementById("root")?.clientWidth / 2 || 0;
        const newY = document.getElementById("root")?.clientHeight / 2 || 0;
        const newViewState = flyToEvent(foundEvent, viewState);
        setViewState(newViewState);
        setClickedObject({
          index: 0,
          x: newX,
          y: newY,
          object: foundEvent,
          coordinate: foundEvent.coordinates,
        });

        // Remove eventId from URL to clean it up
        urlParams.delete('eventId');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }, 200);
      return;
    }
    
    // Wait for data to be loaded for regular events
    if (locationsData.length === 0 && crownClosureData.length === 0) {
      return;
    }
    
    // Search for the event in locationsData
    let foundEvent: IEvent | null = null;
    let foundLocation: ILocation | null = null;
    
    for (const location of locationsData) {
      if (location.events) {
        for (const serverEvent of location.events) {
          if (serverEvent.properties.id === eventId) {
            foundEvent = serverEventToEvent(serverEvent);
            foundLocation = location;
            break;
          }
        }
        if (foundEvent) break;
      }
    }
    
    // Also check crownClosureData
    if (!foundEvent) {
      for (const location of crownClosureData) {
        if (location.events) {
          for (const serverEvent of location.events) {
            if (serverEvent.properties.id === eventId) {
              foundEvent = serverEventToEvent(serverEvent);
              foundLocation = location;
              break;
            }
          }
          if (foundEvent) break;
        }
      }
    }
    
    if (foundEvent) {
      // Mark as restored to prevent multiple restorations
      eventRestoredRef.current = eventId;
      
      // Use setTimeout to ensure DOM is ready and state updates properly
      setTimeout(() => {
        const newX = document.getElementById("root")?.clientWidth / 2 || 0;
        const newY = document.getElementById("root")?.clientHeight / 2 || 0;
        const newViewState = flyToEvent(foundEvent, viewState);
        setViewState(newViewState);
        setClickedObject({
          index: 0,
          x: newX,
          y: newY,
          object: foundEvent,
          coordinate: foundEvent.coordinates,
        });
        
        // Remove eventId from URL to clean it up
        urlParams.delete('eventId');
        const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
        window.history.replaceState({}, '', newUrl);
      }, 200);
    }
  }, [locationsData, crownClosureData, currentUser, clickedObject]);
  
  // Reset the ref when clickedObject is cleared (e.g., when modal is closed)
  useEffect(() => {
    if (clickedObject === null) {
      eventRestoredRef.current = null;
    }
  }, [clickedObject]);

  const addPipeline = (e) => {
    e.stopPropagation();

    setCurrentlyEditingPipeline(true);
  };

  const cancelEditing = (e) => {
    e.stopPropagation();

    // reset edited pipeline
    setEditGeoJson({
      type: "FeatureCollection",
      features: [],
    });

    // stop editing
    setCurrentlyEditingPipeline(false);
  };

  const savePipeline = (e) => {
    e.stopPropagation();
    if ((window as any).userData?.add_pipeline) {
      appContext.services.pipelineService.addPipeline(editGeoJson).then(() => {
        // instead of reloading the whole page:
        // window.location.reload();

        // ... let's try to just reload the pipeline data:
        appContext.services.pipelineService
          .getMyData()
          .then((data: any) => {
            setCurrentPipelineData(data?.data);
          })
          .catch((error: any) => {
            (window as any).encounteredError = true;
          });

        // ... and then stopping the edit mode:
        cancelEditing(e);
      });
    }
  };

  const pipeline = new GeoJsonLayer({
    id: "pipeline-layer",
    data: currentPipelineData,
    parameters: {
      depthTest: false,
    },
    pickable: true,
    autoHighlight: true,
    highlightColor: ({ object }) => {
      return object?.properties.objectType === "pipelineSegment"
        ? PIPELINE_HIGHLIGHT_COLOR
        : PIPELINE_HIGHLIGHT_COLOR_2;
    },
    lineWidthScale: 1,
    lineWidthMinPixels: 5,
    getLineColor: ({
      properties: { objectType },
    }: {
      properties: IPipelineProperties;
    }) => {
      return objectType === "pipelineSegment"
        ? PIPELINE_COLOR_PRIMARY
        : PIPELINE_COLOR_SECONDARY;
    },
    getLineWidth: 5,
  } as any);

  const crownClosurePath = new GeoJsonLayer({
    id: "crownclosurepath-layer",
    data: JSON.parse(JSON.stringify(currentgeojsonData)), // force deep clone
    parameters: {
      depthTest: false,
    },
    pickable: true,
    autoHighlight: false,
    lineWidthScale: 1,
    lineWidthMinPixels: 5,
    getLineColor: ({
      properties: { state },
    }: {
      properties: { state: string };
    }) => {
      return state === "unchecked"
        ? CROWN_HIGHLIGHT_COLOR_2
        : CROWN_HIGHLIGHT_COLOR;
    },
    getLineWidth: 5,
  } as any);

  const segmentLayer = new GeoJsonLayer({
    id: "segment-layer",
    data: segmentsData,
    pickable: false,
    autoHighlight: true,
    lineWidthScale: 1,
    stroked: true,
    lineWidthMinPixels: appContext.projects?.id === "red" ? 4 : 1,
    // lineWidthMaxPixels: 4,
    getLineColor: SEGMENT_COLOR,
    GRID_AREA_COLOR,
    getFillColor: () => [0, 0, 0, 0],
  } as any);

  const mainObjectAoi = new GeoJsonLayer({
    id: "segment-layer",
    data: mainObjectAoiData,
    pickable: false,
    autoHighlight: true,
    lineWidthScale: 1,
    stroked: true,
    lineWidthMinPixels: 1,
    // lineWidthMaxPixels: 4,
    getLineColor: MAIN_OBJECT_AOI_COLOR,
    GRID_AREA_COLOR,
    getFillColor: () => [0, 0, 0, 0],
  } as any);

  const segmentText = new TextLayer({
    id: "grid-text-layer",
    data: segmentsData,
    pickable: true,
    getPosition: (area) => MapLogic.calculatePolygonCenter(area),
    getText: (area) => area.properties.area,
    getSize: 15,
    background: true,
    backgroundPadding: [3, 3, 3, 3],
    getBackgroundColor: [255, 255, 255, 160],
  } as any);

  const gridAreasLayer = new GeoJsonLayer({
    id: "grid-layer",
    data: gridAreaData,
    pickable: false,
    autoHighlight: true,
    lineWidthScale: 1,
    lineWidthMinPixels: 2,
    getLineColor: GRID_AREA_COLOR,
    getFillColor: () => [0, 0, 0, 0],
  } as any);

  const dronePathsLayer = new GeoJsonLayer({
    id: "drone-layer",
    data: dronePaths,
    pickable: true,
    autoHighlight: true,
    lineWidthScale: 1,
    lineWidthMinPixels: 4,
    getLineColor: [0, 255 / 1.5, 0, 255],
    highlightColor: () => [0, 255, 0, 255],
  } as any);

  const gridAreasText = new TextLayer({
    id: "grid-text-layer",
    data: gridAreaData,
    pickable: true,
    getPosition: (area) => MapLogic.calculatePolygonCenter(area),
    getText: (area) => area.properties.name,
    getSize: 15,
    background: true,
    backgroundPadding: [3, 3, 3, 3],
    getBackgroundColor: [255, 255, 255, 160],
  } as any);

  const [eventsFilter, setEventsFilter] = useState<{
    state: ("new" | "active" | "only report change" | "do not report" | "archived")[];
    reportType: ("Processed Reports" | "Edited Reports")[];
    detectedBy: ("Helicopter" | "Drone" | "Satellite" | "Others")[];
    type: string[];
    _version?: number;
    dateFrom?: Date;
    dateTo?: Date;
    eventFilterType?: "Latest" | "By Supervision" | "Oldest" | null;
  }>({
    state: ["active", "only report change", "do not report", "new"],
    reportType: ["Edited Reports", "Processed Reports"],
    detectedBy: ["Drone", "Helicopter", "Satellite", "Others"],
    type: [],
    eventFilterType: "Latest",
  });

  const eventsData = useMemo(() => {
    return locationsData
      .map(({ events, id }) => {
        if (!events || events.length === 0) return null;

        const sorted = [...events].sort(
          (a, b) =>
            new Date(b.properties.date).getTime() -
            new Date(a.properties.date).getTime()
        );

        const newest = sorted[0];
        const oldest = sorted[sorted.length - 1];

        if (eventsFilter.eventFilterType === "Oldest") {
          return serverEventToEvent(newest);
        }

        return serverEventToEvent(newest);
      })
      .filter(Boolean)
      .filter(
        ({ properties: { state } }) =>
          state !== "deleted" &&
          state !== "observe" &&
          state !== "closed" &&
          state !== "removed"
      );
  }, [locationsData, eventsFilter.eventFilterType]);

  const processedCrownClosureData = useMemo(() => {
    return crownClosureData
      .map(({ events }) => {
        const newest = getNewestCrownClosureEvent(events);
        return newest ? serverEventToEvent(newest) : null;
      })
      .filter(Boolean)
      .filter(
        ({ properties: { state } }) =>
          state !== "deleted" &&
          state !== "observe" &&
          state !== "closed" &&
          state !== "removed"
      );
  }, [crownClosureData]);

  const processedMarkersData = useMemo(
    () =>
      markersData.map(
        (marker): IEvent => ({ ...serverEventToEvent(marker), isMarker: true })
      ),
    [markersData]
  );

  const filteredEventsData = useMemo(() => {
    return eventsData.filter((event) => {
      const loc = locationsData.find((l) =>
        l.events?.some((e) => e.properties.id === event?.properties?.id)
      );

      if (!loc) return false;

      const sorted = [...loc.events].sort(
        (a, b) =>
          new Date(b.properties.date).getTime() -
          new Date(a.properties.date).getTime()
      );

      const oldest = sorted[sorted.length - 1];
      const newest = sorted[0];

      // Station events toggle filter
      if (
        !showStations &&
        (isStationEvent(event?.properties) || isStationEvent(newest?.properties))
      ) {
        return false;
      }

      // 🔥 Critical Logic:
      // If "First Report" selected → evaluate filter conditions on oldest,
      // but show newest.
      if (eventsFilter.eventFilterType === "Oldest") {
        if (
          !showStations &&
          (isStationEvent(oldest?.properties) || isStationEvent(newest?.properties))
        ) {
          return false;
        }
        const eventDate = new Date(oldest.properties.date);
        if (eventsFilter.dateFrom && eventDate < eventsFilter.dateFrom)
          return false;
        if (eventsFilter.dateTo && eventDate > eventsFilter.dateTo)
          return false;

        // Other filter conditions - evaluate oldest's state, type, etc.
        if (
          oldest.properties.state &&
          !eventsFilter.state.includes(oldest.properties.state as any)
        )
          return false;

        // Report type filter
        if (eventsFilter.reportType.length) {
          const isProcessed =
            oldest.properties.author?.endsWith("@supervision.earth");

          if (eventsFilter.reportType.length === 1) {
            if (
              eventsFilter.reportType.includes("Processed Reports") &&
              !isProcessed
            ) {
              return false;
            }

            if (
              eventsFilter.reportType.includes("Edited Reports") &&
              isProcessed
            ) {
              return false;
            }
          }
        }

        // DetectedBy filter
        if (eventsFilter.detectedBy.length) {
          const detectedByMap: Record<string, string> = {
            h: "Helicopter",
            d: "Drone",
            s: "Satellite",
            i: "Others",
          };

          let detectedByValue: string | undefined;

          if (oldest?.properties?.detectedBy) {
            detectedByValue = oldest.properties.detectedBy;
          } else {
            const detectedByFlagMatches =
              oldest?.properties?.id.match(/_\w{1}_/gi);

            const detectedByFlag =
              detectedByFlagMatches && detectedByFlagMatches.length > 0
                ? detectedByFlagMatches.pop().replace(/_/g, "")
                : "i";

            detectedByValue = detectedByMap[detectedByFlag];
          }

          if (
            !detectedByValue ||
            !eventsFilter.detectedBy.includes(detectedByValue as any)
          ) {
            return false;
          }
        }

        // Type filter
        if (eventsFilter.type.length > 0) {
          const eventType = oldest?.properties?.type;
          if (!eventsFilter.type.includes(eventType)) {
            return false;
          }
        }

        return true;
      }

      // Normal filters (Latest / By Supervision)
      const state = event?.properties?.state;
      const author = event?.properties?.author;
      const date = new Date(event?.properties?.date);

      if (eventsFilter.eventFilterType === "By Supervision") {
        if (!author || !author.includes("@supervision.earth")) {
          return false;
        }
      }

      if (eventsFilter.dateFrom) {
        if (date < eventsFilter.dateFrom) {
          return false;
        }
      }

      if (eventsFilter.dateTo) {
        if (date > eventsFilter.dateTo) {
          return false;
        }
      }

      // State filter
      if (!eventsFilter.state.includes(state as any)) {
        return false;
      }

      // Report type filter
      if (eventsFilter.reportType.length) {
        const isProcessed = author?.endsWith("@supervision.earth");

        if (eventsFilter.reportType.length === 1) {
          if (
            eventsFilter.reportType.includes("Processed Reports") &&
            !isProcessed
          ) {
            return false;
          }

          if (
            eventsFilter.reportType.includes("Edited Reports") &&
            isProcessed
          ) {
            return false;
          }
        }
      }

      if (eventsFilter.detectedBy.length) {
        const detectedByMap: Record<string, string> = {
          h: "Helicopter",
          d: "Drone",
          s: "Satellite",
          i: "Others",
        };

        let detectedByValue: string | undefined;

        if (event?.properties?.detectedBy) {
          detectedByValue = event.properties.detectedBy;
        } else {
          const detectedByFlagMatches =
            event?.properties?.id.match(/_\w{1}_/gi);

          const detectedByFlag =
            detectedByFlagMatches && detectedByFlagMatches.length > 0
              ? detectedByFlagMatches.pop().replace(/_/g, "")
              : "i";

          detectedByValue = detectedByMap[detectedByFlag];
        }

        if (
          !detectedByValue ||
          !eventsFilter.detectedBy.includes(detectedByValue as any)
        ) {
          return false;
        }
      }

      if (eventsFilter.type.length > 0) {
        const eventType = event?.properties?.type;
        if (!eventsFilter.type.includes(eventType)) {
          return false;
        }
      }

      return true;
    });
  }, [eventsData, eventsFilter, locationsData, showStations]);

  const droneImageLayer = new IconLayer({
    id: "drone-images-layer",
    data: droneImages,
    autoHighlight: true,
    pickable: true,
    onClick: (obj) => {
      getDroneImageUrl(obj.object);
    },
    iconAtlas: DroneIconPng,
    iconMapping: {
      marker: { x: 0, y: 0, width: 512, height: 512, mask: true },
    },
    sizeScale: 4,
    getIcon: () => "marker",
    minSize: 4,
    getColor: (d: IDroneImage) => {
      return d._id === activeDroneImage?._id
        ? [255, 255 / 4, 255 / 8, 255]
        : [255 / 8, 255 / 4, 255, 255];
    },
    getSize: INCIDENT_ICON_SIZE * Math.log(viewState.zoom / INCIDENT_ICON_SIZE),
    getPosition: (d: IDroneImage) => d.geometry.coordinates,
    // getFilterValue: (d: IDroneImage) => d.geometry.coordinates,
    // // filterRange: [100, 5000],
    // filterSoftRange: [0.2, 1],
    // filterTransformSize: true,
    // filterTransformColor: true,
    // extensions: [new DataFilterExtension({ filterSize: 1 })],
  } as any);

  const events = new IconLayer({
    id: "events-layer",
    // data: eventsData
    //   .filter((event) =>
    //     eventsFilter.state === "all"
    //       ? true
    //       : eventsFilter.state === "active"
    //       ? ["active", "new"].includes(event.properties.state)
    //       : event.properties.state === eventsFilter.state
    //   )
    //   .filter((event) =>
    //     eventsFilter.reportType === "All"
    //       ? true
    //       : eventsFilter.reportType === "Processed Reports"
    //       ? event.properties.author.endsWith("@supervision.earth")
    //       : !event.properties.author.endsWith("@supervision.earth")
    //   ),
    data: filteredEventsData,
    pickable: true,
    autoHighlight: true,
    iconAtlas: null,
    getIcon: (d: IEvent) => {
      const { author, id, state, type } = d.properties;

      return {
        url: getEventIcon(id, state, type, author),
        width: 384,
        height: 384,
      };
    },
    sizeScale: 16,
    getSize: 8,
    getPosition: (d: IEvent) => d.coordinates,
    getFilterValue: (d: IEvent) => d.opacity,
    filterRange: [0, 1],
    filterSoftRange: [0.2, 1],
    filterTransformSize: true,
    filterTransformColor: true,
    extensions: [new DataFilterExtension({ filterSize: 1 })],
  } as any);

  const CrownClosureEvents = new IconLayer({
    id: "crownClosureEvents-layer",
    data: processedCrownClosureData,
    pickable: true,
    autoHighlight: true,
    iconAtlas: null,
    getIcon: (d: IEvent) => {
      const { author, id, state, type } = d.properties;

      return {
        url: getEventIcon(id, state, type, author),
        width: 384,
        height: 384,
      };
    },
    sizeScale: 16,
    getSize: 8,
    getPosition: (d: IEvent) => d.coordinates,
    getFilterValue: (d: IEvent) => d.opacity,
    filterRange: [0, 1],
    filterSoftRange: [0.2, 1],
    filterTransformSize: true,
    filterTransformColor: true,
    extensions: [new DataFilterExtension({ filterSize: 1 })],
  } as any);

  const locationIdLayer = new TextLayer({
    id: "text-layer",
    data: filteredEventsData,
    pickable: true,
    getPosition: (event) => event.coordinates,
    getText: (event) => event.properties.locationId,
    getSize: 15,
    background: true,
    backgroundPadding: [3, 3, 3, 3],
    getBackgroundColor: [255, 255, 255, 160],
  } as any);

  const CrownLocationIdLayer = new TextLayer({
    id: "text-layer",
    data: processedCrownClosureData,
    pickable: true,
    getPosition: (event) => event.coordinates,
    getText: (event) => event.properties.locationId,
    getSize: 15,
    background: true,
    backgroundPadding: [3, 3, 3, 3],
    getBackgroundColor: [255, 255, 255, 160],
  } as any);

  const getMapApi = useMemo(() => ({ closeTooltip }), [closeTooltip]);

  // Measurement line layer
  const measurementLineData = useMemo(() => {
    if (measureMode === "map" && measurePoints.length > 0) {
      const lineCoords = measureHoverPoint
        ? [...measurePoints, measureHoverPoint]
        : measurePoints;

      if (lineCoords.length >= 2) {
        return {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: lineCoords,
              },
              properties: {},
            },
          ],
        };
      }
    }
    return null;
  }, [measureMode, measurePoints, measureHoverPoint]);

  const measurementLineLayer = measurementLineData
    ? new GeoJsonLayer({
        id: "measurement-line-layer",
        data: measurementLineData,
        pickable: false,
        lineWidthMinPixels: 3,
        getLineColor: [255, 0, 0, 255],
        getLineWidth: 3,
      } as any)
    : null;

  // Measurement text layer for distance display
  const measurementTextData = useMemo(() => {
    if (measureMode === "map") {
      const coords = measureHoverPoint
        ? [...measurePoints, measureHoverPoint]
        : measurePoints;

      return coords.slice(1).map((point, idx) => {
        const start = coords[idx];
        const midPoint: [number, number] = [
          (start[0] + point[0]) / 2,
          (start[1] + point[1]) / 2,
        ];
        const distance = calculateDistance(start, point);
        return {
          position: midPoint,
          text: formatDistance(distance),
        };
      });
    }
    return [];
  }, [measureMode, measurePoints, measureHoverPoint]);

  const measurementTextLayer =
    measurementTextData.length > 0
      ? new TextLayer({
          id: "measurement-text-layer",
          data: measurementTextData,
          pickable: false,
          getPosition: (d) => d.position,
          getText: (d) => d.text,
          getSize: 16,
          getColor: [255, 0, 0, 255],
          background: true,
          backgroundPadding: [5, 5, 5, 5],
          getBackgroundColor: [255, 255, 255, 200],
        } as any)
      : null;

  const dreamUserEmail =
    currentUser ?? appContext?.services?.firebaseService?.auth?.currentUser?.email;
  const showSviObjectMapEvents = isDreamUser(dreamUserEmail);
  const sviObjectMapEvents = showSviObjectMapEvents ? getSviObjectMapEvents() : [];

  const sviObjectMapEventsLayer =
    sviObjectMapEvents.length > 0
      ? new IconLayer({
          id: "svi-object-map-events-layer",
          data: sviObjectMapEvents,
          pickable: true,
          autoHighlight: true,
          iconAtlas: null,
          getIcon: (d: IEvent) => {
            const iconState =
              d.properties.state === "done" ? "active" : d.properties.state || "active";
            return {
              url: getEventIcon(
                d.properties.id,
                iconState,
                d.properties.type,
                DREAM_USER_EMAIL,
              ),
              width: 384,
              height: 384,
            };
          },
          sizeScale: 16,
          getSize: 8,
          getPosition: (d: IEvent) => d.coordinates,
          updateTriggers: {
            getIcon: dreamUserEmail,
            getPosition: sviObjectMapEvents.length,
          },
          onClick: (info: any) => {
            if (info.object) {
              const clickedEvent = info.object as IEvent;
              const newX = document.getElementById("root")?.clientWidth / 2 || 0;
              const newY = document.getElementById("root")?.clientHeight / 2 || 0;
              const newViewState = flyToEvent(clickedEvent, viewState);
              setViewState(newViewState);
              setClickedObject({
                index: info.index ?? 0,
                x: newX,
                y: newY,
                object: clickedEvent,
                coordinate: clickedEvent.coordinates,
              });
            }
          },
        } as any)
      : null;

  const hanMarkersLayer =
    showMarkers && processedMarkersData.length > 0
      ? new IconLayer({
          id: "han-markers-layer",
          data: processedMarkersData,
          pickable: true,
          autoHighlight: true,
          iconAtlas: null,
          getIcon: () => ({
            url: WhiteMarkerIconPng,
            width: 384,
            height: 384,
          }),
          sizeScale: 16,
          getSize: 8,
          getPosition: (d: IEvent) => d.coordinates,
        } as any)
      : null;

  let layers = [
    pipeline,
    events,
    ShowCrownclosure && crownClosurePath,
    ShowCrownclosure && CrownClosureEvents,
    ShowCrownclosure && labelEnabled && CrownLocationIdLayer,
    labelEnabled && locationIdLayer,
    showSegment && segmentLayer,
    showmainObjectAoi && mainObjectAoi,
    showSegment && segmentText,
    showGridArea && gridAreasLayer,
    showGridArea && gridAreasText,
    showDronePaths && dronePathsLayer,
    showDroneImages && droneImageLayer,
    measurementLineLayer,
    measurementTextLayer,
    sviObjectMapEventsLayer,
    hanMarkersLayer,
  ];

  if (currentlyEditingPipeline) {
    //@ts-ignore
    const pipelineEditingLayer = new EditableGeoJsonLayer({
      id: "edit-pipeline-layer",
      data: editGeoJson,
      mode: DrawLineStringMode,
      onEdit: (data: any) => {
        setEditGeoJson(data.updatedData);
      },
    } as any);

    layers = [pipeline, events, pipelineEditingLayer];
  }

  const detectionOptions = [
    ["Helicopter", language === "de" ? "Helikopter" : "Helicopter"],
    ["Satellite", language === "de" ? "Satellit" : "Satellite"],
    ["Drone", language === "de" ? "Drohne" : "Drone"],
    ["Others", language === "de" ? "Andere" : "Others"],
  ];

  return (
    <ContextMenu point={hoveredObject.coordinate} viewState={viewState}>
      <div
        onClick={(e) => {
          e.preventDefault();
        }}
        onContextMenu={(e) => {
          if (measureMode === "map") {
            e.preventDefault();
            resetMeasurement();
          }
        }}
      >
        <DeckGL
          width="100%"
          height="100%"
          viewState={viewState}
          onViewStateChange={({
            viewState,
            interactionState,
            oldViewState,
          }: {
            viewState: IViewState;
            interactionState: IInteractionState;
            oldViewState: IViewState;
          }) => {
            setViewState(viewState);
          }}
          controller={true}
          layers={layers}
          ContextProvider={MapContext.Provider}
          useDevicePixels={false}
          onHover={(info) => {
            if (
              measureMode === "map" &&
              measurePoints.length > 0 &&
              info.coordinate
            ) {
              setMeasureHoverPoint([info.coordinate[0], info.coordinate[1]]);
            } else if (measureMode === "map") {
              setMeasureHoverPoint(null);
            }
          }}
          onClick={(info, event: any) => {
            // Handle measure mode clicks
            const sourceEvent: any =
              (info as any)?.sourceEvent || event?.srcEvent;
            const isRightClick =
              event?.rightButton ||
              sourceEvent?.which === 3 ||
              sourceEvent?.button === 2;

            if (measureMode === "map") {
              if (isRightClick) {
                event?.preventDefault?.();
                sourceEvent?.preventDefault?.();
                resetMeasurement();
                return;
              }

              if (info.coordinate) {
                const coord: [number, number] = [
                  info.coordinate[0],
                  info.coordinate[1],
                ];
                setMeasurePoints((prev) => {
                  const next = [...prev, coord];
                  const total = calculatePathDistance(next);
                  setCalculatedDistance(total);
                  return next;
                });
                setMeasureHoverPoint(null);
              }
              return;
            }

            setHoveredObject({
              index: info.index,
              x: info.x,
              y: info.y,
              coordinate: info.coordinate,
              object: info.object as IEvent,
            });

            // if we just clicked on a button in an event modal...
            if (preventNextGenericMapClick) {
              setPreventNextGenericMapClick(false);
              // ... do nothing!
              return;
            }

            let newViewState = viewState;
            let actuallyChangedViewState = false;

            const isClickingEvent =
              info != null &&
              info.object != null &&
              (info.object as IEvent).type === "Point";
            const isZoomedIn = viewState.zoom > initialViewState.zoom;

            if (clickedZoomBtn) {
              if (!(isClickingEvent && isZoomedIn)) {
                newViewState = {
                  ...previousViewState,
                  transitionInterpolator: new LinearInterpolator(),
                  transitionInterruption: TRANSITION_EVENTS.BREAK,
                  transitionEasing: ourEasing,
                };
                newViewState.transitionDuration = calcDuration(
                  previousViewState,
                  newViewState
                );
                setClickedZoomBtn(false);
                actuallyChangedViewState = true;
              } else {
                setClickedZoomBtn(false);
              }
            }

            if (info != null) {
              let newX = info.x;
              let newY = info.y;
              let infoObj = info.object as IEvent;
              if (infoObj != null) {
                if (infoObj.type === "Point") {
                  // If already zoomed in, use current viewState to preserve zoom level
                  // Otherwise, use newViewState (which might have been reset by clickedZoomBtn logic)
                  const viewStateForCentering = isZoomedIn
                    ? viewState
                    : newViewState;
                  newViewState = flyToEvent(infoObj, viewStateForCentering);
                  // instead of setting the element to the x and y of the click (which will no longer be accurate
                  // after we fly to the clicked event anyway), we set x and y to the middle of the screen...
                  newX = document.getElementById("root").clientWidth / 2;
                  newY = document.getElementById("root").clientHeight / 2;
                  actuallyChangedViewState = true;
                }

                setClickedObject({
                  index: info.index,
                  x: newX,
                  y: newY,
                  object: infoObj,
                  coordinate: {},
                });

                // hide tooltips on zoom button press (as off-center zooming will lead to the tooltip
                // being aaaanywhere on the map, but not in the correct place!)
                let zoomIns = document.querySelectorAll(
                  ".mapboxgl-ctrl-zoom-in"
                );
                for (let i = 0; i < zoomIns.length; i++) {
                  zoomIns[i].addEventListener("click", function () {
                    closeTooltip();
                  });
                }
                let zoomOuts = document.querySelectorAll(
                  ".mapboxgl-ctrl-zoom-out"
                );
                for (let i = 0; i < zoomOuts.length; i++) {
                  zoomOuts[i].addEventListener("click", function () {
                    closeTooltip();
                  });
                }
              }
            }

            if (actuallyChangedViewState) {
              try {
                setViewState(newViewState);
              } catch (err) {
                // TODO refresh
              }
            }
          }}
        >
          <StaticMap
            mapboxApiAccessToken={env.REACT_APP_MAPBOX_ACCESS_TOKEN}
            mapStyle={mapStyle}
            preventStyleDiffing={true}
          />
          <div
            // className={Style.mapControl}
            style={{
              position: "absolute",
              bottom: "0.5rem",
              left: "0.5rem",
              zIndex: 99999,
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <NavigationControl onViewportChange={setViewState} />
            <Icon
              onClick={toggleMapStyle}
              className={Style.mapStyleButton}
              component={
                mapStyle === normalMapStyle
                  ? () => <SatelliteIcon className={Style.mapButtonIcon} />
                  : () => <WorldIcon className={Style.mapButtonIcon} />
              }
            />
          </div>
        </DeckGL>
        <MapComponentContext.Provider value={getMapApi}>
          {<PipelineTooltip clickedObject={clickedObject} />}
          {
            <EventTooltip
              eventObject={clickedObject?.object}
              eventsData={[...locationsData, ...crownClosureData]}
              refreshEvents={refreshEvents}
              zoomIn={zoomIn}
              openImageCompare={openImageCompare}
              setVideoUrl={setVideoUrl}
              showVideoModal={showVideoModal}
              setShowVideoModal={setShowVideoModal}
            />
          }
          {
            <CompareImages
              images={compareImages}
              closeModal={closeImageCompare}
              eventObject={clickedObject?.object}
              refreshEvents={refreshEvents}
              language={language}
            />
          }
          {
            <AddNewHeliEvent
              enabled={enableHeli}
              setEnableHeli={setEnableHeli}
              refreshEvents={refreshEvents}
              event={hoveredObject}
            />
          }
          {
            <AddCrownClosureEvent
              enabled={enableCc}
              setEnableHeli={setEnableCc}
              refreshEvents={refreshEvents}
              event={hoveredObject}
            />
          }
          {droneImageUrl && (
            <ImageModal
              open={showDroneImage}
              setOpen={setShowDroneImage}
              imageUrl={droneImageUrl}
              setImageUrl={setDroneImageUrl}
            />
          )}
          {videoUrl && (
            <VideoModal
              open={showVideoModal}
              setOpen={setShowVideoModal}
              videoUrl={videoUrl}
              setVideoUrl={setVideoUrl}
            />
          )}
        </MapComponentContext.Provider>

        {currentlyEditingPipeline && (
          <div className={Style.bottomBannerContainer}>
            <span className={Style.bottomBanner}>
              <i>{language === "de" ? "Anmerkung" : "Note"}:</i>&nbsp;
              {language === "de"
                ? "Pipeline-Segmente können per Doppelklick abgeschlossen werden."
                : "You can use a double click to finalize a pipeline segment."}
            </span>
          </div>
        )}
        {/* Other control buttons - positioned on the right side */}
        <div
          style={{
            position: "absolute",
            right: "0px",
            minWidth: "200px",
            display: "flex",
            alignItems: "flex-end",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            padding: "0.25rem",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-end",
              gap: "0.25rem",
              marginTop: "4px",
            }}
          >
            {/* Options Dropdown */}
            <div
              className={`${Style.topRightSwitch} ${Style.buttonOptions}`}
              style={{ marginTop: "4px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => setOptionsCollapsed(!optionsCollapsed)}
                >
                  <b>{language === "de" ? "Optionen" : "Options"}</b>
                  <span style={{ fontSize: "12px", marginLeft: "8px" }}>
                    {optionsCollapsed ? "▼" : "▲"}
                  </span>
                </div>
                {!optionsCollapsed && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginTop: "8px",
                    }}
                  >
                    <div
                      className={`${Style.topRightSwitch} ${Style.buttonLocationID}`}
                    >
                      {/* Location ID */}
                      {language === "de" ? "Ereignis-ID anzeigen" : "Location ID"}
                      <Switch onChange={() => setLabelEnabled(!labelEnabled)} />
                    </div>
                    {!embeddedApp && (
                      <>
                        {currentUser === "hansegas@supervision.earth" ||
                        currentUser === "inetz@supervision.earth" ||
                        currentUser === "ngn@supervision.earth" ||
                        currentUser === "edis@supervision.earth" ||
                        currentUser === "shn@supervision.earth" ||  currentUser === "ten@supervision.earth"? (
                          <div
                            className={`${Style.topRightSwitch} ${Style.buttonAddEvent}`}
                          >
                            {language === "de"
                              ? "Kronenschluss hinzufügen"
                              : "Add Crown Closure event"}
                            <Switch
                              onChange={(checked) => {
                                setEnableCc(checked);
                              }}
                              checked={enableCc}
                            />
                          </div>
                        ) : null}

                        <div
                          className={`${Style.topRightSwitch} ${Style.buttonAddEvent}`}
                        >
                          {/* Add Event */}
                          {language === "de" ? "Ereignis hinzufügen" : "Add Event"}
                          <Switch
                            onChange={(checked) => {
                              setEnableHeli(checked);
                            }}
                            checked={enableHeli}
                          />
                        </div>
                      </>
                    )}

                    <div
                      className={`${Style.topRightSwitch} ${Style.buttonMonitoredArea}`}
                    >
                      {/* Monitored area */}
                      {language === "de" ? "Überwachter Bereich" : "Monitored area"}
                      <Switch
                        checked={showSegment}
                        disabled={!appContext.projects?.id}
                        onChange={async (checked) => {
                          if (checked) {
                            getSegmentsData();
                          } else {
                            setShowSegment(false);
                          }
                        }}
                      />
                    </div>
                    {!embeddedApp && (
                      <div
                        className={`${Style.topRightSwitch} ${Style.buttonGridArea}`}
                      >
                        {/* Grid area */}
                        {embeddedApp
                          ? "AOIs Area"
                          : language === "de"
                          ? "Netzbereiche"
                          : "Grid area"}

                        <Switch
                          checked={showGridArea}
                          disabled={!appContext.projects?.id}
                          onChange={async (checked) => {
                            if (checked) {
                              getGridAreasData();
                            } else {
                              setShowGridArea(false);
                            }
                          }}
                        />
                      </div>
                    )}
                    {appContext.projects?.droneImageEnabled && (
                      <div
                        className={`${Style.topRightSwitch} ${Style.buttonDroneImages}`}
                      >
                        {language === "de" ? "Flugspuren" : "Drone images"}
                        <Switch
                          style={{ margin: "0 .5rem" }}
                          checked={showDroneImages}
                          disabled={!appContext.projects?.id}
                          onChange={async (checked) => {
                            if (checked) {
                              getDroneImages();
                            } else {
                              setShowDroneImages(false);
                            }
                          }}
                        />
                      </div>
                    )}

                    {!embeddedApp && (
                      <div
                        className={`${Style.topRightSwitch} ${Style.buttonFlightPath}`}
                      >
                        {language === "de" ? "Flugspuren" : "Flight path"}
                        <Switch
                          style={{ margin: "0 .5rem" }}
                          checked={showDronePaths}
                          disabled={!appContext.projects?.id}
                          onChange={async (checked) => {
                            if (checked) {
                              getDroneJobs();
                            } else {
                              setShowDronePaths(false);
                            }
                          }}
                        />
                      </div>
                    )}

                    {/* Crown Closure path toggle */}
                    {!embeddedApp &&
                      isCrownClosureProject(appContext.projects?.id) && (
                        <div className={Style.topRightSwitch}>
                          {language === "de" ? "Kronenschluss" : "Crown Closure"}
                          <Switch
                            style={{ margin: "0 .5rem" }}
                            checked={ShowCrownclosure}
                            disabled={!appContext.projects?.id}
                            onChange={async (checked) => {
                              if (checked) {
                                setShowCrownclosure(true);
                              } else {
                                setShowCrownclosure(false);
                              }
                            }}
                          />
                        </div>
                      )}

                    {/* Station Events toggle */}
                    {!embeddedApp &&
                      (isHanProject(appContext.projects?.id) ||
                        canShowStations() ||
                        !!(window as any)._userData?.show_stations ||
                        showStations ||
                        eventsData.some((e) => isStationEvent(e.properties))) && (
                        <div className={Style.topRightSwitch}>
                          {language === "de"
                            ? "Stationsereignisse"
                            : "Station events"}
                          <Switch
                            style={{ margin: "0 .5rem" }}
                            checked={showStations}
                            disabled={!appContext.projects?.id}
                            onChange={handleToggleStations}
                          />
                        </div>
                      )}

                    {/* Marker Events toggle */}
                    {!embeddedApp &&
                      (isHanProject(appContext.projects?.id) ||
                        !!(window as any)._userData?.show_markers ||
                        showMarkers ||
                        markersData.length > 0) && (
                        <div className={Style.topRightSwitch}>
                          {language === "de"
                            ? "Marker-Ereignisse"
                            : "Marker events"}
                          <Switch
                            style={{ margin: "0 .5rem" }}
                            checked={showMarkers}
                            disabled={!appContext.projects?.id}
                            onChange={handleToggleMarkers}
                          />
                        </div>
                      )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {!embeddedApp && (
            <>
              <div
                className={`${Style.topRightSwitch} ${Style.buttonMeasureDistance}`}
                style={{ marginTop: "4px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setMeasureDistanceCollapsed(!measureDistanceCollapsed)
                    }
                  >
                    <b>
                      {language === "de"
                        ? "Entfernung messen"
                        : "Measure Distance"}
                    </b>
                    <span style={{ fontSize: "12px", marginLeft: "8px" }}>
                      {measureDistanceCollapsed ? "▼" : "▲"}
                    </span>
                  </div>
                  {!measureDistanceCollapsed && (
                    <>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          marginTop: "8px",
                        }}
                      >
                        <Radio.Group
                          value={measureMode}
                          onChange={(e) => {
                            const newMode = e.target.value;
                            setMeasureMode(newMode);
                            if (newMode !== "map") {
                              resetMeasurement();
                            }
                            if (newMode === "manual") {
                              setCalculatedDistance(null);
                            }
                            if (newMode === "none") {
                              setManualPoint1({ lat: "", lng: "" });
                              setManualPoint2({ lat: "", lng: "" });
                            }
                          }}
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <Radio value="none" style={{ margin: "0" }}>
                            {language === "de" ? "Aus" : "Off"}
                          </Radio>
                          <Radio value="map" style={{ margin: "0" }}>
                            {language === "de"
                              ? "Auf Karte messen"
                              : "Measure in Map"}
                          </Radio>
                          <Radio value="manual" style={{ margin: "0" }}>
                            {language === "de"
                              ? "Manuell messen"
                              : "Measure Manually"}
                          </Radio>
                        </Radio.Group>
                      </div>
                      {measureMode === "map" && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                            marginTop: "8px",
                            padding: "4px",
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        >
                          <div style={{ fontSize: "12px", color: "white" }}>
                            {measurePoints.length === 0
                              ? language === "de"
                                ? "Klicken Sie auf die Karte, um Punkte hinzuzufügen (Rechtsklick zum Löschen)"
                                : "Click on the map to add points (right-click to clear)"
                              : calculatedDistance !== null
                              ? `${
                                  language === "de"
                                    ? "Gesamtdistanz"
                                    : "Total distance"
                                }: ${formatDistance(calculatedDistance)}`
                              : language === "de"
                              ? "Fügen Sie weitere Punkte hinzu (Rechtsklick zum Löschen)"
                              : "Add more points (right-click to clear)"}
                          </div>
                          {measurePoints.length > 0 && (
                            <Button
                              size="small"
                              type="primary"
                              onClick={resetMeasurement}
                              style={{ marginTop: "4px" }}
                            >
                              {language === "de" ? "Zurücksetzen" : "Reset"}
                            </Button>
                          )}
                        </div>
                      )}
                      {measureMode === "manual" && (
                        <div
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            marginTop: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <b style={{ fontSize: "12px", color: "white" }}>
                              {language === "de" ? "Punkt 1" : "Point 1"}
                            </b>
                            <div style={{ display: "flex", gap: "4px" }}>
                              <Input
                                placeholder={
                                  language === "de" ? "Breitengrad" : "Latitude"
                                }
                                size="small"
                                value={manualPoint1.lat}
                                onChange={(e) =>
                                  setManualPoint1({
                                    ...manualPoint1,
                                    lat: e.target.value,
                                  })
                                }
                                style={{ width: "50%" }}
                              />
                              <Input
                                placeholder={
                                  language === "de" ? "Längengrad" : "Longitude"
                                }
                                size="small"
                                value={manualPoint1.lng}
                                onChange={(e) =>
                                  setManualPoint1({
                                    ...manualPoint1,
                                    lng: e.target.value,
                                  })
                                }
                                style={{ width: "50%" }}
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}
                          >
                            <b style={{ fontSize: "12px", color: "white" }}>
                              {language === "de" ? "Punkt 2" : "Point 2"}
                            </b>
                            <div style={{ display: "flex", gap: "4px" }}>
                              <Input
                                placeholder={
                                  language === "de" ? "Breitengrad" : "Latitude"
                                }
                                size="small"
                                value={manualPoint2.lat}
                                onChange={(e) =>
                                  setManualPoint2({
                                    ...manualPoint2,
                                    lat: e.target.value,
                                  })
                                }
                                style={{ width: "50%" }}
                              />
                              <Input
                                placeholder={
                                  language === "de" ? "Längengrad" : "Longitude"
                                }
                                size="small"
                                value={manualPoint2.lng}
                                onChange={(e) =>
                                  setManualPoint2({
                                    ...manualPoint2,
                                    lng: e.target.value,
                                  })
                                }
                                style={{ width: "50%" }}
                              />
                            </div>
                          </div>
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => {
                              const lat1 = parseFloat(manualPoint1.lat);
                              const lng1 = parseFloat(manualPoint1.lng);
                              const lat2 = parseFloat(manualPoint2.lat);
                              const lng2 = parseFloat(manualPoint2.lng);

                              if (
                                !isNaN(lat1) &&
                                !isNaN(lng1) &&
                                !isNaN(lat2) &&
                                !isNaN(lng2) &&
                                lat1 >= -90 &&
                                lat1 <= 90 &&
                                lat2 >= -90 &&
                                lat2 <= 90 &&
                                lng1 >= -180 &&
                                lng1 <= 180 &&
                                lng2 >= -180 &&
                                lng2 <= 180
                              ) {
                                const distance = calculateDistance(
                                  [lng1, lat1],
                                  [lng2, lat2]
                                );
                                setCalculatedDistance(distance);
                              } else {
                                alert(
                                  language === "de"
                                    ? "Bitte geben Sie gültige Koordinaten ein (Breitengrad: -90 bis 90, Längengrad: -180 bis 180)"
                                    : "Please enter valid coordinates (Latitude: -90 to 90, Longitude: -180 to 180)"
                                );
                              }
                            }}
                          >
                            {language === "de"
                              ? "Entfernung berechnen"
                              : "Calculate Distance"}
                          </Button>
                          {calculatedDistance !== null && (
                            <div
                              style={{
                                padding: "4px",
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                borderRadius: "4px",
                                fontSize: "12px",
                                textAlign: "center",
                              }}
                            >
                              <b style={{ color: "white" }}>
                                {language === "de" ? "Entfernung" : "Distance"}:{" "}
                                {formatDistance(calculatedDistance)}
                              </b>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div
                className={`${Style.topRightSwitch} ${Style.buttonEventsFilter}`}
                style={{ marginTop: "4px" }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      setEventsFilterCollapsed(!eventsFilterCollapsed)
                    }
                  >
                    <b>
                      {language === "de" ? "Events filtern" : "Events filter"}
                    </b>
                    <span style={{ fontSize: "12px", marginLeft: "8px" }}>
                      {eventsFilterCollapsed ? "▼" : "▲"}
                    </span>
                  </div>
                  {!eventsFilterCollapsed && (
                    <>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <b style={{ color: "white", alignSelf: "center" }}>
                          {language === "de" ? "Datum" : "Date"}
                        </b>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <div style={{ position: "relative", width: "120px" }}>
                              <DatePicker
                                allowClear={false}
                                style={{ width: "100%", padding: 1 }}
                                value={eventsFilter.dateFrom ? moment(eventsFilter.dateFrom) : null}
                                onChange={(value) => {
                                  setEventsFilter((p) => ({
                                    ...p,
                                    dateFrom: value ? value.toDate() : null,
                                    eventFilterType: value || p.dateTo ? p.eventFilterType : null,
                                  }));
                                }}
                              />
                              {eventsFilter.dateFrom && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEventsFilter((p) => ({
                                      ...p,
                                      dateFrom: null,
                                      eventFilterType: p.dateTo ? p.eventFilterType : null,
                                    }))
                                  }
                                  style={{
                                    position: "absolute",
                                    right: 6,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    padding: 0,
                                    lineHeight: 1,
                                    color: "#000",
                                    fontSize: "14px",
                                    zIndex: 10,
                                    pointerEvents: "auto",
                                  }}
                                >
                                  ❌
                                </button>
                              )}
                            </div>
                            <p
                              style={{
                                margin: "auto",
                                fontSize: "1rem",
                                textTransform: "capitalize",
                                color: "white",
                              }}
                            >
                              {language === "de" ? "von" : "from"}
                            </p>
                          </div>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <div style={{ position: "relative", width: "120px" }}>
                              <DatePicker
                                allowClear={false}
                                style={{
                                  width: "100%",
                                  padding: 1,
                                  boxSizing: "border-box",
                                }}
                                value={eventsFilter.dateTo ? moment(eventsFilter.dateTo) : null}
                                onChange={(value) => {
                                  setEventsFilter((p) => ({
                                    ...p,
                                    dateTo: value ? value.toDate() : null,
                                    eventFilterType: value || p.dateFrom ? p.eventFilterType : null,
                                  }));
                                }}
                              />
                              {eventsFilter.dateTo && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEventsFilter((p) => ({
                                      ...p,
                                      dateTo: null,
                                      eventFilterType: p.dateFrom ? p.eventFilterType : null,
                                    }))
                                  }
                                  style={{
                                    position: "absolute",
                                    right: 6,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    padding: 0,
                                    lineHeight: 1,
                                    color: "#000",
                                    fontSize: "14px",
                                    zIndex: 10,
                                    pointerEvents: "auto",
                                  }}
                                >
                                  ❌
                                </button>
                              )}
                            </div>
                            <p
                              style={{
                                margin: "auto",
                                fontSize: "1rem",
                                textTransform: "capitalize",
                                color: "white",
                              }}
                            >
                              {language === "de" ? "bis" : "to"}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          paddingTop: "8px",
                        }}
                      >
                        {(() => {
                          const noDateSelected =
                            !eventsFilter.dateFrom && !eventsFilter.dateTo;
                          return (
                            <Radio.Group
                              value={
                                noDateSelected
                                  ? null
                                  : eventsFilter.eventFilterType
                              }
                              onChange={(e) => {
                                if (noDateSelected) return;
                                setEventsFilter((p) => ({
                                  ...p,
                                  eventFilterType: e.target.value,
                                }));
                              }}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Radio
                                value="Latest"
                                disabled={noDateSelected}
                                style={{ margin: "0" }}
                              >
                                {language === "de"
                                  ? "Letzter Kommentar"
                                  : "Latest comment"}
                              </Radio>
                              <Radio
                                value="By Supervision"
                                disabled={noDateSelected}
                                style={{ margin: "0" }}
                              >
                                {language === "de"
                                  ? "Letztes Update"
                                  : "Latest update"}
                              </Radio>
                              <Radio
                                value="Oldest"
                                disabled={noDateSelected}
                                style={{ margin: "0" }}
                              >
                                {language === "de"
                                  ? "Erster Bericht"
                                  : "First Report"}
                              </Radio>
                            </Radio.Group>
                          );
                        })()}
                      </div>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <b style={{ color: "white", alignSelf: "center" }}>
                          {language === "de" ? "Status" : "State"}
                        </b>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                          defaultValue={eventsFilter.state}
                        >
                          {[
                            ["active", "Aktiv"],
                            ["only report change", "Nur Änderung Melden"],
                            ["do not report", "Nicht mehr melden"],
                            ["archived", "Archiviert"],
                          ].map((state, idx) => (
                            <Checkbox
                              key={idx}
                              style={{ margin: "0" }}
                              checked={eventsFilter.state.includes(
                                state[0] as any
                              )}
                              onClick={(e) => {
                                setEventsFilter((p) => ({
                                  ...p,
                                  state: p.state.includes(state[0] as any)
                                    ? p.state.filter((s) => s !== state[0])
                                    : [...p.state, state[0] as any],
                                }));
                              }}
                            >
                              {language === "de" ? state[1] : state[0]}
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <b style={{ alignSelf: "center", color: "white" }}>
                          {language === "de" ? "Erkannt durch" : "Detected By"}
                        </b>

                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          {detectionOptions.map(([value, label]) => (
                            <Checkbox
                              key={value}
                              style={{ margin: "0" }}
                              checked={eventsFilter.detectedBy.includes(
                                value as any
                              )}
                              onClick={() => {
                                setEventsFilter((p) => ({
                                  ...p,
                                  detectedBy: p.detectedBy.includes(
                                    value as any
                                  )
                                    ? p.detectedBy.filter((s) => s !== value)
                                    : [...p.detectedBy, value as any],
                                  _version: Date.now(), // optional
                                }));
                              }}
                            >
                              {label}
                            </Checkbox>
                          ))}
                        </div>
                      </div>

                      {/* <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <b>{language === "de" ? "Event Type" : "Event Type"}</b>
                    <pre>{JSON.stringify(eventsFilter.type)}</pre>
                    <Checkbox.Group
                      options={appContext?.projects?.types}
                      value={eventsFilter.type}
                      onChange={(checkedValues) => {
                        setEventsFilter((prev) => {
                          return {
                            ...prev,
                            type: [...checkedValues] as string[],
                            _version: Date.now() // ensure new array reference
                          };
                        });
                      }}
                      style={{ width: "150px", textAlign: "left" }}
                    />
                  </div> */}
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <b style={{ alignSelf: "center", color: "white" }}>
                          {language === "de" ? "Event Typ" : " Event Type "}
                        </b>
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          {(isHanProject(appContext?.projects?.id) && showStations
                            ? getHanEventTypes(appContext?.projects?.types)
                            : appContext?.projects?.types
                          )?.map((type) => (
                            <Checkbox
                              key={type}
                              style={{ margin: "0" }}
                              checked={eventsFilter.type.includes(type)}
                              onClick={(e) => {
                                setEventsFilter((p) => ({
                                  ...p,
                                  type: p.type.includes(type)
                                    ? p.type.filter((s) => s !== type)
                                    : [...p.type, type],
                                }));
                              }}
                            >
                              {type}
                            </Checkbox>
                          ))}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <b style={{ alignSelf: "center", color: "white" }}>
                          {language === "de" ? "Bearbeitet" : "Edited"}
                        </b>
                        <div
                          defaultValue={eventsFilter.reportType}
                          style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {[
                            // ["All", "All"],
                            ["Processed Reports", "Unbearbeitete Meldungen"],
                            ["Edited Reports", "Bearbeitete Meldungen"],
                          ].map((reportType, idx) => (
                            <Checkbox
                              key={idx}
                              style={{ margin: "0" }}
                              checked={eventsFilter.reportType.includes(
                                reportType[0] as any
                              )}
                              onClick={(e) => {
                                setEventsFilter((p) => ({
                                  ...p,
                                  reportType: p.reportType.includes(
                                    reportType[0] as any
                                  )
                                    ? p.reportType.filter(
                                        (s) => s !== reportType[0]
                                      )
                                    : [...p.reportType, reportType[0] as any],
                                }));
                              }}
                            >
                              {language === "de"
                                ? reportType[1]
                                : reportType[0]}
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {showDronePaths && droneJobs.length && (
          <div
            style={{
              position: "absolute",
              zIndex: 30,
              left: "2px",
              top: "2px",
              background: "#8c8c8c",
              borderColor: "black",
              paddingBlock: "0.4rem",
              display: "flex",
              flexDirection: "column",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {droneJobs.map((d, idx) => (
              <Checkbox
                key={idx + d.jobId}
                style={{ margin: "0 .5rem", color: "white" }}
                checked={selectedJob?.jobId === d.jobId}
                value={d.jobId}
                onChange={async (e) => {
                  if (e.target.checked) {
                    setSelectedJob(d);
                    getDronePaths(appContext.projects?.id, d.jobId);
                  } else {
                    setSelectedJob(null);
                    setdronePaths([]);
                  }
                }}
              >
                {d.jobId}
              </Checkbox>
            ))}
          </div>
        )}
      </div>
    </ContextMenu>
  );
}





